// src/pages/MyListing.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import CheckCircle from "@mui/icons-material/CheckCircle"; // verified badge
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import { IconButton, Tooltip } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../components/authContext";

import { auth } from "../components/firebaseConfig";
import { useRequireListing } from "../hooks/useRequireListing";

import { signOut } from "firebase/auth";



import "./MyListing.css";



const API_BASE_URL = process.env.REACT_APP_BASE_URL; // e.g. http://localhost:5000
const CLIENT_BASE_URL = process.env.CLIENT_BASE_URL;

function buildPublicUrl(base, username) {
  const handle = String(username || "").trim();
  if (!handle) return "";
  const origin = (base || window.location.origin).replace(/\/+$/, "");
  try {
    const u = new URL(origin);
    u.pathname = `/${handle}`;
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    return `${origin}/${handle}`;
  }
}

export default function MyListing() {
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const { checking: checkingListing, allowed: hasListing } = useRequireListing();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [showCopy, setShowCopy] = useState(false);
  const [copied, setCopied] = useState(false);
  const urlInputRef = useRef(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);



  // Guard: if no user (after first render), send home
  useEffect(() => {
    if (!authReady) return;
    if (user === null) {
      navigate("/login", { replace: true, state: { from: "/dashboard" } });
    }
  }, [user, authReady, navigate]);

  // Load creator dashboard data
  useEffect(() => {
    let alive = true;
    (async () => {

      if (!authReady || !user || !hasListing) return;
      try {
        setLoading(true);
        const token = await user.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/my/listing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || "Failed to load listing.");
        if (!alive) return;
        setData(json);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user, authReady, hasListing]);

  const isApproved = data?.status === "accepted";
  const avatar = data?.profilePictureUrl || "";
  const name = data?.displayName;
  const pendingCount = data?.pendingRequestsCount || 0;
  const hasPending = pendingCount > 0;
  const stripeIncomplete = !data?.stripeOnboardingComplete;




  const publicUrl = useMemo(() => {
    // prefer server-provided link if you later add it, otherwise construct it
    if (data?.publicUrl) return data.publicUrl;
    return buildPublicUrl(CLIENT_BASE_URL, data?.username);
  }, [data]);

  const canCopy = isApproved && Boolean(data?.username);

  const copyNow = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
    } catch {
      window.prompt("Copy your public link:", publicUrl); // ultimate fallback
    }
  };

  const revealAndCopy = async () => {
    if (!canCopy) {
      return;
    }
    setShowCopy(true);
    // copy immediately and select the text
    await copyNow();
    setTimeout(() => urlInputRef.current?.select(), 0);
  };

  const goEdit = () => navigate("/edit/listing");
  const openPreview = () => navigate("/preview");
  const openSupport = () => navigate("/support");

  // computed label for the button
  const payoutsLabel = (() => {
    if (data?.stripeOnboardingComplete) return "Edit Payouts (Stripe)";
    if (data?.stripeToken) return "Finish Payout Setup";
    return "Set Up Payouts (Stripe)";
  })();

  // optional help copy under the button (only when not complete)
  const payoutsHelp = (() => {
    if (data?.stripeOnboardingComplete) return "";
    if (data?.stripeToken) return "Finish setting up Stripe to receive payments for completed activities.";
    return "Setting up Stripe is required to receive payments for completed activities.";
  })();

  const payoutsCta = async () => {
    if (!isApproved || payoutsLoading) return; // you already disable the button, but guard anyway
    try {
      setPayoutsLoading(true);
      const idToken = await user.getIdToken();
      const r = await fetch(`${API_BASE_URL}/api/my/payouts`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json"
        }
      });
      const j = await r.json();
      if (!r.ok || !j?.url) setError("Could not open Stripe. Please try again.")
      // Stripe account links are one-time; open in same tab
      window.location.href = j.url;
    } catch (e) {
      setPayoutsLoading(false);
      setError(e.message || "Could not open Stripe. Please try again.")
    }
  };

  const logOut = async () => {
    try {
      await signOut(auth);
    } finally {
      navigate("/", { replace: true });
    }
  };

  if (!user || checkingListing) {
    // Very brief placeholder during auth resolution or prevalidation
    return <div className="ml-container"><div className="ml-card">Loading...</div></div>;
  }

  if (loading) {
    return (
      <div className="ml-container">
        <div className="ml-card">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ml-container">
        <div className="ml-card">
          <div className="ml-error">An error occurred while loading this page, if you continue to see message this please contact support.</div>
          <button className="ml-btn ml-btn-secondary" onClick={() => window.location.reload()}>
            Contact Support
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-container">
      <div className="ml-card">
        {/* Header */}
        <div className="ml-head">
          <div className="ml-avatar-wrap">
            {avatar ? (
              <img className="ml-avatar" src={avatar} alt={`${name} avatar`} />
            ) : (
              <div className="ml-avatar ml-avatar-fallback">🙂</div>
            )}
          </div>
          <div className="ml-name-row">
            <h1 className="ml-name">{name}</h1>
            {isApproved && (
              <CheckCircle
                className="ml-verified"
                fontSize="small"
                titleAccess="Verified"
                aria-label="Verified"
              />
            )}
          </div>

          {/* Status chip / card */}
          {isApproved ? (
            <div className="ml-live-banner" role="status" aria-live="polite">
              <div className="ml-live-copy">
                <div className="ml-live-title">Your page is live 🎉</div>
              </div>

            </div>
          ) : (
            <div className="ml-status-card">
              <div className="ml-status-title">Page Pending</div>
              <div className="ml-status-sub">
                Your page is now under review. Some functionality is disabled until approval.
                We’ll email you once we have an update on your status.
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="ml-actions">
          <button
            className={`ml-btn ml-btn-secondary ml-btn-row ${!isApproved ? "ml-btn-locked" : ""} ${hasPending ? "ml-btn-brown" : ""}`}
            onClick={() => navigate("/requests")}
            disabled={!isApproved}
          >
            <span className="ml-btn-label">View Requests</span>
            {hasPending && (
              <span className="ml-badge-right ml-badge-light" aria-label={`${pendingCount} pending requests`} />
            )}
            {!isApproved && <span className="ml-lock" aria-hidden>🔒</span>}
          </button>

          <button className="ml-btn ml-btn-secondary" onClick={goEdit}>
            Edit Page
          </button>

          {/* Copy Public Link */}
          {!showCopy ? (
            <button
              className={`ml-btn ml-btn-secondary ${!canCopy ? "ml-btn-locked" : ""}`}
              onClick={revealAndCopy}
              disabled={!canCopy}
            >
              Copy Page Link
              {!canCopy && <span className="ml-lock" aria-hidden>🔒</span>}
            </button>
          ) : (
            <div className="ml-copy-row" role="group" aria-label="Public link">
              <input
                ref={urlInputRef}
                className="ml-copy-input"
                value={publicUrl}
                readOnly
                onClick={(e) => e.currentTarget.select()}
                aria-readonly="true"
              />
              <div className="ml-copy-actions">
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    className="ml-copy-btn"
                    onClick={copyNow}
                    aria-label="Copy page link"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {copied && (
                  <DoneIcon
                    className="ml-copied"
                    fontSize="small"
                    aria-label="Copied"
                    titleAccess="Copied"
                  />
                )}

              </div>
            </div>
          )}

          <button className="ml-btn ml-btn-secondary" onClick={openPreview}>
            Preview Public Page
          </button>

          <button
            className={`ml-btn ml-btn-secondary ml-btn-row ${!isApproved ? "ml-btn-locked" : ""} ${stripeIncomplete && !hasPending ? "ml-btn-brown" : ""}`}
            onClick={payoutsCta}
            disabled={!isApproved || payoutsLoading}
            aria-busy={payoutsLoading ? "true" : "false"}
          >
            {payoutsLoading && <span className="ml-btn-spinner" aria-hidden="true" />}
            <span className="ml-btn-label">{payoutsLoading ? "Opening Stripe…" : payoutsLabel}</span>
            {stripeIncomplete && hasPending && (
              <span className="ml-badge-right ml-badge-brown" aria-label={`${pendingCount} pending requests`} />
            )}
            {stripeIncomplete && !hasPending && (
              <span className="ml-badge-right ml-badge-light" aria-hidden="true" />
            )}
            {!isApproved && <span className="ml-lock" aria-hidden>🔒</span>}
          </button>

          {!data?.stripeOnboardingComplete && (
            <div className="ml-help">{payoutsHelp}</div>
          )}


          {/* Support + Sign Out side-by-side */}
          <div className="ml-actions-row">
            <button className="ml-btn ml-btn-text" onClick={openSupport}>
              Support
            </button>
            <button className="ml-btn ml-btn-text" onClick={logOut}>
              Sign Out
            </button>
          </div>
          <div className="ml-footer-links">
            <Link className="ml-footer-link" to="/trust-safety" target="_blank" rel="noreferrer">Trust &amp; Safety</Link>
            <span className="home-legal-dot">·</span>

            <Link className="ml-footer-link" to="/terms" target="_blank" rel="noreferrer">Terms</Link>
            <span className="home-legal-dot">·</span>

            <Link className="ml-footer-link" to="/privacy" target="_blank" rel="noreferrer">Privacy</Link>
          </div>

        </div>

        {/* Optional mini summary */}
        {!!data?.activities?.length && (
          <div className="ml-mini">
            <div className="ml-mini-title">Your Activities</div>
            <ul className="ml-mini-list">
              {data.activities.slice(0, 3).map((a) => (
                <li key={a.id} className="ml-mini-item">
                  <div className="ml-mini-item-title">{a.title}</div>
                  <div className="ml-mini-item-sub">
                    ${a.price} • {a.durationLabel}
                  </div>
                </li>
              ))}
            </ul>
            {data.activities.length > 3 && (
              <div className="ml-mini-more">+{data.activities.length - 3} more</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
